import { Box, Button, Paper, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { format } from 'date-fns'
import React from 'react'
import { useParams } from 'react-router-dom'

import { Notification } from '@components/prereg_validation/notification'
import { useSessionContext } from '@providers/session'
import { i18WithParams as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { Hooks, Types } from 'service-api'

export const Agreements = () => {
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const { navigateRoute } = useRoutes()
  const contracts = Hooks.useClientContracts(locationName, Types.ContractType.ServiceAgreement)

  const lup = Hooks.useClientLocationUserProfile(locationName, sessionCtx.state.user?.lupId || '', [
    'agreements.contract',
  ])

  const handleAgreementView = (contractId: string) => {
    navigateRoute('profileContracts', { contractId })
  }

  const renderContracts = (contract: Types.ContractClientDto) => (
    <Paper elevation={1} sx={{ p: 2, mt: 1 }} key={contract.id}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6">{contract.displayName}</Typography>
        <Button variant="contained" size="medium" color="primary" onClick={() => handleAgreementView(contract.id!)}>
          {t('profile.agreements.signAgreement')}
        </Button>
      </Stack>
    </Paper>
  )

  const renderSignedContracts = (agreement: Types.AgreementClientDto) => {
    const contract = contracts?.data?.results?.find((c) => c.id === agreement.contractId)

    if (!contract) {
      return null
    }

    return (
      <Paper elevation={1} sx={{ p: 2, mt: 1 }} key={agreement.id}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h6">{contract.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('profile.agreements.signedAt', {
                date: format(new Date(agreement.updatedAt!), 'M/d/yyyy'),
                name: agreement.customerName || '',
              })}
            </Typography>
          </div>
          <Button variant="text" size="medium" color="primary" onClick={() => handleAgreementView(contract.id!)}>
            {t('profile.agreements.viewContract')}
          </Button>
        </Stack>
      </Paper>
    )
  }

  const renderAgreements = () => {
    if (contracts.isSuccess && contracts.data) {
      const signedAgreements = lup.data?.agreements || []
      const signedAgreementsIdSet = new Set(signedAgreements.map(({ contractId }) => contractId))
      const contractsIdSet = new Set(contracts.data.results.map(({ id }) => id))

      const contractsData = contracts.data.results.filter((c) => !signedAgreementsIdSet.has(c.id))
      const itemsToRender: React.ReactNode[] = [
        ...contractsData.map(renderContracts),
        ...signedAgreements.filter((agreement) => contractsIdSet.has(agreement.contractId)).map(renderSignedContracts),
      ]

      return itemsToRender
    }

    return null
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Typography variant="h2" sx={{ mb: 2 }} pl={isBelowMD ? 2 : 0}>
        {t('profile.agreements.title')}
      </Typography>
      {lup.isLoading || !lup.data ? (
        <Skeleton variant="rectangular" width="100%" height={200} />
      ) : (
        <>{renderAgreements()}</>
      )}
    </Box>
  )
}
